import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Stack,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/AddOutlined";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  fetchHeadings,
  fetchResolvedByOptions,
  addHeading,
  removeHeading,
  addResolvedByOption,
  removeResolvedByOption,
} from "./ticketsSlice";

const fieldSx = { "& .MuiInputBase-input": { fontSize: "0.8125rem" } };

export default function MasterListsDialog({ open, onClose }) {
  const dispatch = useDispatch();
  const [tab, setTab] = useState(0);
  const [draft, setDraft] = useState("");
  const { headings, resolvedByOptions } = useSelector((s) => s.tickets);

  useEffect(() => {
    if (open) {
      dispatch(fetchHeadings());
      dispatch(fetchResolvedByOptions());
      setDraft("");
    }
  }, [open, dispatch]);

  useEffect(() => {
    setDraft("");
  }, [tab]);

  const list = tab === 0 ? headings : resolvedByOptions;

  const onAdd = async () => {
    const name = draft.trim();
    if (!name) return;
    const action = tab === 0 ? addHeading(name) : addResolvedByOption(name);
    const res = await dispatch(action);
    const ok =
      tab === 0
        ? addHeading.fulfilled.match(res)
        : addResolvedByOption.fulfilled.match(res);
    if (ok) {
      setDraft("");
      toast.success("Added");
    } else {
      toast.error(res.payload || "Failed");
    }
  };

  const onRemove = async (item) => {
    const action = tab === 0 ? removeHeading(item._id) : removeResolvedByOption(item._id);
    const res = await dispatch(action);
    const ok =
      tab === 0
        ? removeHeading.fulfilled.match(res)
        : removeResolvedByOption.fulfilled.match(res);
    if (ok) toast.success("Removed");
    else toast.error(res.payload || "Failed");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "2px" } }}
    >
      <DialogTitle
        sx={{
          p: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="h5">Master Lists</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          minHeight: 32,
          borderBottom: "1px solid",
          borderColor: "divider",
          "& .MuiTab-root": { minHeight: 32, fontSize: "0.75rem" },
        }}
      >
        <Tab label="Headings" />
        <Tab label="Resolved By" />
      </Tabs>
      <DialogContent sx={{ p: 1.5 }}>
        <Stack direction="row" spacing={1} mb={1.5}>
          <TextField
            fullWidth
            size="small"
            placeholder={tab === 0 ? "New heading…" : "New option…"}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onAdd();
              }
            }}
            sx={fieldSx}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon fontSize="small" />}
            onClick={onAdd}
            disabled={!draft.trim()}
          >
            Add
          </Button>
        </Stack>

        {list.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            No entries yet.
          </Typography>
        ) : (
          <List dense disablePadding>
            {list.map((item) => (
              <ListItem
                key={item._id}
                sx={{
                  px: 1,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
                secondaryAction={
                  <Tooltip title="Remove">
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => onRemove(item)}
                    >
                      <DeleteIcon
                        fontSize="small"
                        sx={{ color: "error.main" }}
                      />
                    </IconButton>
                  </Tooltip>
                }
              >
                <ListItemText
                  primary={item.name}
                  primaryTypographyProps={{ fontSize: "0.8125rem" }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions
        sx={{ p: 1.25, borderTop: "1px solid", borderColor: "divider" }}
      >
        <Button onClick={onClose} variant="outlined">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
